import type { Chat } from '@/data/types';
import type { ChatsAccess } from '@/lib/auth';

// The list filters group chats by who acts next (Gary, 18 Oct: the old Needs reply, Open, Unpaid,
// Stale, Unread and Admin chips overlapped and confused). Every chat sits in exactly one of Needs
// you, Dr C or Waiting on customer. Money owing and the 24 hours stay on the row's status line.
// Nothing owed and quiet for 7 days: archived.

export type Next = 'you' | 'drc' | 'customer';

/**
 * The list's three tabs (Gary, 19 Oct): Reply, a person owes a reply now; Leo, Leo is in a conversation (the
 * customer wrote in the last 24 hours); Quiet, nothing owed. Every open chat is in exactly one. Unread is a dot on
 * the row, not a tab: an unread chat with nothing owed is Quiet.
 */
export type Tab = 'reply' | 'leo' | 'quiet';

const LEO_ACTIVE_MS = 24 * 60 * 60 * 1000;

export function tabOf(c: Chat, now: number): Tab {
  const owed = c.dueMinutes !== undefined && !c.answered;
  if (owed || c.aiState === 'human_required' || c.markedUnread) return 'reply';
  if (c.aiState === 'ai' && !!c.lastInboundAt && now - new Date(c.lastInboundAt).getTime() < LEO_ACTIVE_MS) return 'leo';
  return 'quiet';
}

/** Who has to act next: a person, Dr C, or the customer. */
export function whoNext(c: Chat): Next {
  if (c.aiState === 'ai') return 'drc';
  const waiting = c.dueMinutes !== undefined && !c.answered;
  if (waiting || c.aiState === 'human_required' || c.unread > 0 || c.markedUnread) return 'you';
  return 'customer';
}

/** No operator has the chat yet (head office). Team chats are never anyone's lead. */
export function hasNoOwner(c: Chat): boolean {
  return !c.owner && !c.isTeam;
}

/**
 * A customer waiting to be read: new messages, or marked unread. The bottom tab, the filter chips and the
 * browser tab all count customers this way; a chat row counts its own messages (Gary, 18 Oct).
 */
export function isUnread(c: Pick<Chat, 'unread' | 'markedUnread'>): boolean {
  return c.unread > 0 || !!c.markedUnread;
}

export const ARCHIVE_AFTER_DAYS = 7;
const DAY_MS = 24 * 60 * 60 * 1000;

/** Quiet for 7 days with nothing owed. A new message moves the last activity, so it comes back. */
export function autoArchived(c: { unpaid?: boolean; lastActivityAt?: string }, now: number): boolean {
  if (c.unpaid || !c.lastActivityAt) return false;
  return now - new Date(c.lastActivityAt).getTime() > ARCHIVE_AFTER_DAYS * DAY_MS;
}

export type CustomerStatus = 'Unpaid' | 'New' | 'Amber' | 'Bronze' | 'Silver' | 'Gold' | 'Platinum';

/**
 * The customer's status chip (Gary, 18 Oct): Unpaid while an invoice is open, New with no customer record
 * (every lead), otherwise their loyalty tier by its own name.
 */
export function customerStatus(c: Pick<Chat, 'isTeam' | 'unpaid' | 'customerId' | 'tier'>): CustomerStatus | null {
  if (c.isTeam) return null;
  if (c.unpaid) return 'Unpaid';
  // No customer record yet (a lead), or a customer not yet serviced: New.
  if (!c.customerId || !c.tier) return 'New';
  return (c.tier.charAt(0).toUpperCase() + c.tier.slice(1)) as CustomerStatus;
}

/**
 * Whether this person sees a customer chat in their lists and counts. Head office sees all. Others see their brands'
 * chats with no inbox, and any inbox chat assigned to them (Gary, 23 Oct: Barry never saw Cara's Customer service
 * chat, though it was his and Leo told her Barry would reply).
 */
export function seesChat(c: Pick<Chat, 'inbox' | 'brand' | 'ownerId'>, access: ChatsAccess, me: string | undefined): boolean {
  if (access.role === 'head_office') return true;
  return access.brands.includes(c.brand) && (!c.inbox || (!!me && c.ownerId === me));
}
